import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Mail, Filter, ChevronLeft, ChevronRight, Search as SearchIcon } from 'lucide-react';
import api from '../api';
import DateRangeBar from '../components/DateRangeBar';

const EVENT_LABELS = {
  collected: 'Collected', at_depot: 'At depot', out_for_delivery: 'Out for delivery',
  delivered: 'Delivered', failed_delivery: 'Failed delivery',
};
const STATUS_STYLE = {
  sent:    'bg-green-100 text-green-700',
  failed:  'bg-red-100 text-red-700',
  skipped: 'bg-gray-100 text-gray-500',
  bounced: 'bg-red-100 text-red-700',
};
const PAGE_SIZE = 50;

export default function EmailLog() {
  const today = new Date().toISOString().split('T')[0];
  const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString().split('T')[0];
  const [from, setFrom] = useState(weekAgo);
  const [to, setTo] = useState(today);
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  const { data, isLoading } = useQuery({
    queryKey: ['email-log', from, to, status, search, page],
    queryFn: () => api.get('/notifications/log', {
      params: { from, to, status: status !== 'all' ? status : undefined, search: search.trim() || undefined, limit: PAGE_SIZE, offset: page * PAGE_SIZE },
    }).then((r) => r.data),
    keepPreviousData: true,
  });

  const rows = data?.rows || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Mail size={22} className="text-orange-500" />
        <div>
          <h1 className="text-xl font-bold text-gray-800">Email Log</h1>
          <p className="text-xs text-gray-400">Every parcel-status email — sent, failed, or skipped (and why)</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
        <DateRangeBar from={from} to={to} setFrom={setFrom} setTo={setTo} onChange={() => setPage(0)} />
        <div className="flex items-center gap-3 flex-wrap">
          <Filter size={14} className="text-gray-400" />
          {['all', 'sent', 'failed', 'skipped'].map((s) => (
            <button key={s} onClick={() => { setStatus(s); setPage(0); }}
              className={`text-xs px-3 py-1.5 rounded-full border capitalize ${status === s ? 'bg-gray-800 text-white border-gray-800' : 'bg-white border-gray-200 text-gray-600 hover:border-orange-400'}`}>
              {s}
            </button>
          ))}
          <div className="relative">
            <SearchIcon size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              placeholder="Search email, tracking no…"
              className="border rounded-lg pl-7 pr-3 py-1.5 text-xs w-56 focus:outline-none focus:ring-2 focus:ring-orange-400" />
          </div>
          <span className="text-xs text-gray-400 ml-auto">{total.toLocaleString()} {total === 1 ? 'email' : 'emails'}</span>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {isLoading ? <div className="p-10 text-center text-gray-400">Loading…</div>
        : rows.length === 0 ? <div className="p-10 text-center text-gray-400">No parcel emails in this period</div>
        : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                  <tr>
                    <th className="px-4 py-3 text-left whitespace-nowrap">When</th>
                    <th className="px-4 py-3 text-left">Parcel</th>
                    <th className="px-4 py-3 text-left">To</th>
                    <th className="px-4 py-3 text-left">Update</th>
                    <th className="px-4 py-3 text-left">Result</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {rows.map((r) => (
                    <tr key={r.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">
                        {new Date(r.sent_at).toLocaleString('en-ZA', { dateStyle: 'short', timeStyle: 'short' })}
                      </td>
                      <td className="px-4 py-3">
                        {r.tracking_number
                          ? <a href={`/parcels/${r.tracking_number}`} className="font-mono text-xs font-semibold text-orange-500 hover:underline">{r.tracking_number}</a>
                          : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-xs">{r.recipient || <span className="text-gray-300">no address</span>}</td>
                      <td className="px-4 py-3 text-gray-600 text-xs">{EVENT_LABELS[r.event_type] || r.event_type}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-block text-xs px-2 py-1 rounded-full font-medium ${STATUS_STYLE[r.status] || 'bg-gray-100 text-gray-600'}`}>
                          {r.status}
                        </span>
                        {r.detail && <span className="text-xs text-gray-400 ml-2">{r.detail}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
                <p className="text-xs text-gray-500">Page {page + 1} of {totalPages}</p>
                <div className="flex gap-2">
                  <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0} className="p-1.5 rounded border disabled:opacity-40 hover:bg-white"><ChevronLeft size={14} /></button>
                  <button onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1} className="p-1.5 rounded border disabled:opacity-40 hover:bg-white"><ChevronRight size={14} /></button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
